/**
 * UtilityPanel — C004 Utility showcase
 * R12 #317: Utility catalog card (all utilities listed)
 * R12 #318: Expanded to 7 demos (FE-side pure JS, no BE for pure-JS demos)
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { showcaseApi } from '../api/showcaseApi';
import type {
  DateTimeConvertResponse,
  StringMaskResponse,
  ApiError,
} from '../types/showcase';

type UtilTab = 'catalog' | 'datetime' | 'string' | 'client';

const TIMEZONES = [
  'Asia/Seoul',
  'UTC',
  'America/New_York',
  'Europe/London',
  'Asia/Tokyo',
  'America/Los_Angeles',
];

const STRING_MASK_TYPES: { value: 'email' | 'phone' | 'card'; label: string; placeholder: string }[] = [
  { value: 'email', label: 'Email', placeholder: 'user@example.com' },
  { value: 'phone', label: 'Phone (KR)', placeholder: '010-1234-5678' },
  { value: 'card',  label: 'Card No.', placeholder: '1234-5678-9012-3456' },
];

const SPIN_CSS = `@keyframes spin { to { transform: rotate(360deg); } }`;

// ─── Utility catalog entries (#317) ──────────────────────────────────────────

interface UtilEntry {
  className: string;
  // queue #653 §R653-3: 하드코딩 한글 대신 i18n 키를 보관 — 렌더 시 t() 로 lookup(EN 잔여 한글 방지).
  descriptionKey: string;
  locationKey: string;
  hasDemo: boolean;
}

const UTILITY_CATALOG: UtilEntry[] = [
  { className: 'StringUtils',     descriptionKey: 'stringUtils',     locationKey: 'coreUtil',           hasDemo: true },
  { className: 'DateUtils',       descriptionKey: 'dateUtils',       locationKey: 'coreUtil',           hasDemo: true },
  { className: 'MoneyUtils',      descriptionKey: 'moneyUtils',      locationKey: 'coreUtil',           hasDemo: true },
  { className: 'CollectionUtils', descriptionKey: 'collectionUtils', locationKey: 'coreUtil',           hasDemo: false },
  { className: 'IdGenerator',     descriptionKey: 'idGenerator',     locationKey: 'coreUtil',           hasDemo: false },
  { className: 'Preconditions',   descriptionKey: 'preconditions',   locationKey: 'coreUtil',           hasDemo: false },
  { className: 'JsonUtils',       descriptionKey: 'jsonUtils',       locationKey: 'coreUtil',           hasDemo: true },
  { className: 'EnumUtils',       descriptionKey: 'enumUtils',       locationKey: 'coreUtil',           hasDemo: false },
  { className: 'RegexUtils',      descriptionKey: 'regexUtils',      locationKey: 'coreUtil',           hasDemo: false },
  { className: 'ValidatorUtils',  descriptionKey: 'validatorUtils',  locationKey: 'coreUtilClientSide', hasDemo: true },
];

export function UtilityPanel() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<UtilTab>('catalog');

  return (
    <section aria-labelledby="utility-panel-title">
      <style>{SPIN_CSS}</style>
      <h2
        id="utility-panel-title"
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-2)',
          marginTop: 0,
        }}
      >
        {t('showcase.utility.title')}
      </h2>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-4)' }}>
        {t('showcase.utility.subtitle')}
      </p>

      {/* Tabs */}
      <div
        role="tablist"
        aria-label={t('showcase.utility.tabsLabel')}
        style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--color-surface-border)', marginBottom: 'var(--spacing-4)', flexWrap: 'wrap' }}
      >
        {([
          { key: 'catalog',  label: t('showcase.utility.tab.catalog') },
          { key: 'datetime', label: t('showcase.utility.tab.datetime') },
          { key: 'string',   label: t('showcase.utility.tab.string') },
          { key: 'client',   label: t('showcase.utility.tab.client') },
        ] as const).map(({ key, label }) => (
          <button
            key={key}
            role="tab"
            type="button"
            aria-selected={tab === key}
            aria-controls={`utility-tab-${key}`}
            onClick={() => setTab(key)}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'transparent',
              border: 'none',
              borderBottom: tab === key ? '2px solid var(--color-brand-primary)' : '2px solid transparent',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: tab === key ? 700 : 400,
              color: tab === key ? 'var(--color-brand-primary)' : 'var(--color-text-secondary)',
              marginBottom: -2,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div id={`utility-tab-${tab}`} role="tabpanel">
        {tab === 'catalog'  && <CatalogTab />}
        {tab === 'datetime' && <DateTimeTab />}
        {tab === 'string'   && <StringMaskTab />}
        {tab === 'client'   && <ClientDemosTab />}
      </div>

      {/* Learning */}
      <div
        style={{
          padding: 'var(--spacing-3)',
          background: 'var(--color-surface-elevated)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
          marginTop: 'var(--spacing-4)',
        }}
      >
        <strong>{t('showcase.panel.learning')}</strong>{' '}{t('showcase.utility.learning')}
      </div>
    </section>
  );
}

// ─── CatalogTab (#317) ────────────────────────────────────────────────────────

function CatalogTab() {
  const { t } = useTranslation();
  return (
    <div>
      <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-3)' }}>
        {t('showcase.utility.catalog.header')}
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table
          aria-label={t('showcase.utility.catalog.tableAriaLabel')}
          style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-xs)' }}
        >
          <thead>
            <tr style={{ background: 'var(--color-surface-muted)' }}>
              {[
                t('showcase.utility.table.className'),
                t('showcase.utility.table.description'),
                t('showcase.utility.table.location'),
                t('showcase.utility.table.demo'),
              ].map((h) => (
                <th
                  key={h}
                  scope="col"
                  style={{
                    padding: 'var(--spacing-2) var(--spacing-3)',
                    borderBottom: '2px solid var(--color-surface-border)',
                    textAlign: 'left',
                    fontWeight: 700,
                    color: 'var(--color-text-secondary)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {UTILITY_CATALOG.map((entry, idx) => (
              <tr
                key={entry.className}
                style={{ background: idx % 2 === 0 ? 'var(--color-surface-background)' : 'var(--color-surface-muted)' }}
              >
                <td style={{ padding: 'var(--spacing-2) var(--spacing-3)', borderBottom: '1px solid var(--color-surface-border)' }}>
                  <code style={{ fontFamily: 'var(--font-family-mono)', color: 'var(--color-brand-primary)', fontWeight: 700 }}>
                    {entry.className}
                  </code>
                </td>
                <td style={{ padding: 'var(--spacing-2) var(--spacing-3)', borderBottom: '1px solid var(--color-surface-border)', color: 'var(--color-text-secondary)' }}>
                  {t(`showcase.utility.entries.${entry.descriptionKey}.description`)}
                </td>
                <td style={{ padding: 'var(--spacing-2) var(--spacing-3)', borderBottom: '1px solid var(--color-surface-border)', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                  {t(`showcase.utility.location.${entry.locationKey}`)}
                </td>
                <td style={{ padding: 'var(--spacing-2) var(--spacing-3)', borderBottom: '1px solid var(--color-surface-border)', textAlign: 'center' }}>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '1px var(--spacing-2)',
                      borderRadius: 'var(--radius-full)',
                      fontSize: 10,
                      fontWeight: 700,
                      background: entry.hasDemo ? 'var(--color-brand-primary)' : 'var(--color-surface-border)',
                      color: entry.hasDemo ? 'var(--color-text-inverse)' : 'var(--color-text-muted)',
                    }}
                  >
                    {entry.hasDemo ? t('showcase.utility.table.yes') : t('showcase.utility.table.no')}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── DateTimeTab ──────────────────────────────────────────────────────────────

function DateTimeTab() {
  const { t } = useTranslation();
  const [value, setValue] = useState('2026-05-06T12:00:00');
  const [fromTz, setFromTz] = useState('Asia/Seoul');
  const [toTz, setToTz] = useState('UTC');

  const { mutate, isPending, data, isError, error, reset } = useMutation<
    DateTimeConvertResponse,
    ApiError,
    { value: string; fromTz: string; toTz: string }
  >({
    mutationFn: (req) => showcaseApi.utilDatetime(req),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    reset();
    mutate({ value, fromTz, toTz });
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: 'grid', gap: 'var(--spacing-4)', marginBottom: 'var(--spacing-4)' }}>
        <div>
          <label
            htmlFor="dt-value"
            style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}
          >
            {t('showcase.utility.datetime.value')}
          </label>
          <input
            id="dt-value"
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="2026-05-06T12:00:00"
            disabled={isPending}
            style={inputStyle}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-3)' }}>
          <div>
            <label
              htmlFor="dt-from"
              style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}
            >
              {t('showcase.utility.datetime.fromTz')}
            </label>
            <select id="dt-from" value={fromTz} onChange={(e) => setFromTz(e.target.value)} disabled={isPending} style={selectStyle}>
              {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
          <div>
            <label
              htmlFor="dt-to"
              style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}
            >
              {t('showcase.utility.datetime.toTz')}
            </label>
            <select id="dt-to" value={toTz} onChange={(e) => setToTz(e.target.value)} disabled={isPending} style={selectStyle}>
              {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-4)' }}>
        <button type="submit" disabled={isPending || !value.trim()} aria-busy={isPending} style={primaryBtnStyle(isPending || !value.trim())}>
          {isPending && <SpinIcon />}
          {t('showcase.utility.execute')}
        </button>
        {data && (
          <button type="button" onClick={() => { reset(); }} style={secondaryBtnStyle}>
            {t('showcase.common.reset')}
          </button>
        )}
      </div>

      {data && (
        <div
          role="region"
          aria-label={t('showcase.utility.datetime.resultLabel')}
          style={resultCardStyle('var(--color-success)')}
        >
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--spacing-1) var(--spacing-4)', fontSize: 'var(--font-size-sm)' }}>
            <dt style={dtStyle}>{t('showcase.utility.datetime.original')}</dt>
            <dd style={{ margin: 0 }}><code style={codeStyle}>{data.original}</code></dd>
            <dt style={dtStyle}>{t('showcase.utility.datetime.fromTz')}</dt>
            <dd style={{ margin: 0 }}><code style={codeStyle}>{data.fromTz}</code></dd>
            <dt style={dtStyle}>{t('showcase.utility.datetime.toTz')}</dt>
            <dd style={{ margin: 0 }}><code style={codeStyle}>{data.toTz}</code></dd>
            <dt style={dtStyle}>{t('showcase.utility.datetime.converted')}</dt>
            <dd style={{ margin: 0 }}><code style={{ ...codeStyle, color: 'var(--color-brand-primary)', fontWeight: 700 }}>{data.converted}</code></dd>
            <dt style={dtStyle}>epoch ms</dt>
            <dd style={{ margin: 0 }}><code style={codeStyle}>{data.epochMs}</code></dd>
          </dl>
        </div>
      )}

      {isError && error && (
        <div role="alert" style={errorCardStyle}>
          <strong style={{ color: 'var(--color-danger)' }}>{(error as ApiError).code}</strong>
          <p style={{ margin: 'var(--spacing-1) 0 0', color: 'var(--color-text-primary)', fontSize: 'var(--font-size-sm)' }}>{(error as ApiError).message}</p>
        </div>
      )}
    </form>
  );
}

// ─── StringMaskTab ────────────────────────────────────────────────────────────

function StringMaskTab() {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [type, setType] = useState<'email' | 'phone' | 'card'>('email');

  const { mutate, isPending, data, isError, error, reset } = useMutation<
    StringMaskResponse,
    ApiError,
    { value: string; type: 'email' | 'phone' | 'card' }
  >({
    mutationFn: (req) => showcaseApi.utilString(req),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    reset();
    mutate({ value, type });
  }

  const currentPlaceholder = STRING_MASK_TYPES.find((s) => s.value === type)?.placeholder ?? '';

  return (
    <form onSubmit={handleSubmit}>
      {/* Type selector */}
      <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-4)', flexWrap: 'wrap' }}>
        {STRING_MASK_TYPES.map((s) => (
          <label
            key={s.value}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: `1px solid ${type === s.value ? 'var(--color-brand-primary)' : 'var(--color-surface-border)'}`,
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
              background: type === s.value ? 'var(--color-sidebar-active-bg)' : 'transparent',
              color: type === s.value ? 'var(--color-brand-primary)' : 'var(--color-text-primary)',
            }}
          >
            <input type="radio" value={s.value} checked={type === s.value} onChange={() => setType(s.value)} style={{ accentColor: 'var(--color-brand-primary)' }} />
            {s.label}
          </label>
        ))}
      </div>

      {/* Value input */}
      <div style={{ marginBottom: 'var(--spacing-4)' }}>
        <label
          htmlFor="sm-value"
          style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}
        >
          {t('showcase.utility.string.value')}
        </label>
        <input
          id="sm-value"
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={currentPlaceholder}
          disabled={isPending}
          style={inputStyle}
        />
        {/* Tooltip-style hint */}
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--spacing-1)' }}>
          {type === 'email' && t('showcase.utility.string.hint.email')}
          {type === 'phone' && t('showcase.utility.string.hint.phone')}
          {type === 'card'  && t('showcase.utility.string.hint.card')}
        </p>
      </div>

      <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-4)' }}>
        <button type="submit" disabled={isPending || !value.trim()} aria-busy={isPending} style={primaryBtnStyle(isPending || !value.trim())}>
          {isPending && <SpinIcon />}
          {t('showcase.utility.execute')}
        </button>
        {data && (
          <button type="button" onClick={() => { reset(); setValue(''); }} style={secondaryBtnStyle}>
            {t('showcase.common.reset')}
          </button>
        )}
      </div>

      {data && (
        <div role="region" aria-label={t('showcase.utility.string.resultLabel')} style={resultCardStyle('var(--color-success)')}>
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--spacing-1) var(--spacing-4)', fontSize: 'var(--font-size-sm)' }}>
            <dt style={dtStyle}>{t('showcase.utility.string.original')}</dt>
            <dd style={{ margin: 0 }}><code style={codeStyle}>{data.original}</code></dd>
            <dt style={dtStyle}>{t('showcase.utility.string.masked')}</dt>
            <dd style={{ margin: 0 }}><code style={{ ...codeStyle, color: 'var(--color-brand-primary)', fontWeight: 700 }}>{data.masked}</code></dd>
            <dt style={dtStyle}>{t('showcase.utility.string.type')}</dt>
            <dd style={{ margin: 0 }}><code style={codeStyle}>{data.type}</code></dd>
          </dl>
        </div>
      )}

      {isError && error && (
        <div role="alert" style={errorCardStyle}>
          <strong style={{ color: 'var(--color-danger)' }}>{(error as ApiError).code}</strong>
          <p style={{ margin: 'var(--spacing-1) 0 0', color: 'var(--color-text-primary)', fontSize: 'var(--font-size-sm)' }}>{(error as ApiError).message}</p>
        </div>
      )}
    </form>
  );
}

// ─── ClientDemosTab (#318) — pure FE demos (no BE call) ──────────────────────
// queue #656 §R656-3: compute 반환을 { key, params } 디스크립터로 바꿔 렌더부에서 t() 로 해석
// (문자열을 직접 반환하면 EN 로케일에서도 한글이 그대로 노출되므로 금지).

// export 는 vitest 단위 테스트용(compute 가 디스크립터를 반환함을 UI 밖에서 직접 검증).
export interface UtilityResult {
  key: string;
  params?: Record<string, unknown>;
}

interface ClientDemoItem {
  id: string;
  titleKey: string;
  labelKey: string;
  placeholderKey: string;
  compute: (input: string) => UtilityResult;
}

export const CLIENT_DEMOS: ClientDemoItem[] = [
  {
    id: 'emailMask',
    titleKey: 'showcase.utility.demo.emailMask.title',
    labelKey: 'showcase.utility.demo.emailMask.label',
    placeholderKey: 'showcase.utility.demo.emailMask.placeholder',
    compute: (v) => {
      const at = v.indexOf('@');
      if (at < 2) return { key: 'showcase.utility.result.emailMask.value', params: { masked: v } };
      const local = v.slice(0, at);
      const domain = v.slice(at);
      const keep = Math.min(2, local.length);
      const masked = local.slice(0, keep) + '*'.repeat(local.length - keep) + domain;
      return { key: 'showcase.utility.result.emailMask.value', params: { masked } };
    },
  },
  {
    id: 'isoDate',
    titleKey: 'showcase.utility.demo.isoDate.title',
    labelKey: 'showcase.utility.demo.isoDate.label',
    placeholderKey: 'showcase.utility.demo.isoDate.placeholder',
    compute: (v) => {
      try {
        const d = isNaN(Number(v)) ? new Date(v) : new Date(Number(v));
        if (isNaN(d.getTime())) return { key: 'showcase.utility.result.date.formatError' };
        return { key: 'showcase.utility.result.isoDate.ok', params: { iso: d.toISOString() } };
      } catch {
        return { key: 'showcase.utility.result.date.formatError' };
      }
    },
  },
  {
    id: 'currency',
    titleKey: 'showcase.utility.demo.currency.title',
    labelKey: 'showcase.utility.demo.currency.label',
    placeholderKey: 'showcase.utility.demo.currency.placeholder',
    compute: (v) => {
      const n = parseFloat(v.replace(/,/g, ''));
      if (isNaN(n)) return { key: 'showcase.utility.result.currency.invalidNumber' };
      const amount = new Intl.NumberFormat('ko-KR').format(n);
      return { key: 'showcase.utility.result.currency.ok', params: { amount } };
    },
  },
  {
    id: 'businessNo',
    titleKey: 'showcase.utility.demo.businessNo.title',
    labelKey: 'showcase.utility.demo.businessNo.label',
    placeholderKey: 'showcase.utility.demo.businessNo.placeholder',
    compute: (v) => {
      const digits = v.replace(/[^0-9]/g, '');
      if (digits.length !== 10) return { key: 'showcase.utility.result.businessNo.needTenDigits' };
      const weights = [1, 3, 7, 1, 3, 7, 1, 3, 5];
      let sum = 0;
      for (let i = 0; i < 9; i++) sum += parseInt(digits[i]) * weights[i];
      sum += Math.floor((parseInt(digits[8]) * 5) / 10);
      const check = (10 - (sum % 10)) % 10;
      const valid = check === parseInt(digits[9]);
      return { key: valid ? 'showcase.utility.result.businessNo.valid' : 'showcase.utility.result.businessNo.invalidChecksum' };
    },
  },
  {
    id: 'phone',
    titleKey: 'showcase.utility.demo.phone.title',
    labelKey: 'showcase.utility.demo.phone.label',
    placeholderKey: 'showcase.utility.demo.phone.placeholder',
    compute: (v) => {
      const ok = /^0[0-9]{1,2}-[0-9]{3,4}-[0-9]{4}$/.test(v);
      return { key: ok ? 'showcase.utility.result.phone.valid' : 'showcase.utility.result.phone.invalidFormat' };
    },
  },
  {
    id: 'fileExt',
    titleKey: 'showcase.utility.demo.fileExt.title',
    labelKey: 'showcase.utility.demo.fileExt.label',
    placeholderKey: 'showcase.utility.demo.fileExt.placeholder',
    compute: (v) => {
      const allowed = ['png', 'jpg', 'jpeg', 'gif', 'pdf', 'xlsx', 'csv', 'txt', 'zip'];
      const ext = v.split('.').pop()?.toLowerCase() ?? '';
      if (!ext) return { key: 'showcase.utility.result.fileExt.noExtension' };
      const ok = allowed.includes(ext);
      return ok
        ? { key: 'showcase.utility.result.fileExt.allowed', params: { ext } }
        : { key: 'showcase.utility.result.fileExt.blocked', params: { ext, allowed: allowed.join(', ') } };
    },
  },
  {
    id: 'jsonParse',
    titleKey: 'showcase.utility.demo.jsonParse.title',
    labelKey: 'showcase.utility.demo.jsonParse.label',
    placeholderKey: 'showcase.utility.demo.jsonParse.placeholder',
    compute: (v) => {
      try {
        const result = JSON.parse(v);
        return { key: 'showcase.utility.result.jsonParse.ok', params: { pretty: JSON.stringify(result, null, 2) } };
      } catch (e) {
        return { key: 'showcase.utility.result.jsonParse.parseError', params: { message: (e as Error).message } };
      }
    },
  },
];

function ClientDemosTab() {
  const { t } = useTranslation();
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [results, setResults] = useState<Record<string, UtilityResult>>({});

  function runDemo(demo: ClientDemoItem) {
    const val = inputs[demo.id] ?? '';
    setResults((prev) => ({ ...prev, [demo.id]: demo.compute(val) }));
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--spacing-4)' }}>
      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', margin: 0 }}>
        {t('showcase.utility.clientDemos.subtitle')}
      </p>
      {CLIENT_DEMOS.map((demo) => (
        <div
          key={demo.id}
          style={{
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-3)',
            background: 'var(--color-surface-background)',
          }}
        >
          <p style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-brand-primary)', margin: '0 0 var(--spacing-2) 0', fontWeight: 700 }}>
            {t(demo.titleKey)}
          </p>
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <label
                htmlFor={`client-demo-${demo.id}`}
                style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 'var(--spacing-1)' }}
              >
                {t(demo.labelKey)}
              </label>
              <input
                id={`client-demo-${demo.id}`}
                type="text"
                placeholder={t(demo.placeholderKey)}
                value={inputs[demo.id] ?? ''}
                onChange={(e) => setInputs((prev) => ({ ...prev, [demo.id]: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runDemo(demo); } }}
                style={{ ...inputStyle, fontSize: 'var(--font-size-xs)' }}
              />
            </div>
            <button
              type="button"
              onClick={() => runDemo(demo)}
              style={{
                padding: 'var(--spacing-2) var(--spacing-3)',
                background: 'var(--color-brand-primary)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-xs)',
                fontFamily: 'var(--font-family-sans)',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {t('showcase.utility.execute')}
            </button>
          </div>
          {results[demo.id] !== undefined && (
            <pre
              style={{
                marginTop: 'var(--spacing-2)',
                padding: 'var(--spacing-2)',
                background: 'var(--color-surface-muted)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--font-size-xs)',
                fontFamily: 'var(--font-family-mono)',
                color: 'var(--color-text-primary)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                margin: 'var(--spacing-2) 0 0 0',
              }}
            >
              {t(results[demo.id].key, results[demo.id].params)}
            </pre>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Shared style helpers ─────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-3)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontFamily: 'var(--font-family-mono)',
  background: 'var(--color-surface-background)',
  color: 'var(--color-text-primary)',
  boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-2) var(--spacing-3)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontFamily: 'var(--font-family-sans)',
  background: 'var(--color-surface-background)',
  color: 'var(--color-text-primary)',
};

function primaryBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: 'var(--spacing-3) var(--spacing-6)',
    background: disabled ? 'var(--color-text-muted)' : 'var(--color-brand-primary)',
    color: 'var(--color-text-inverse)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'var(--font-family-sans)',
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
  };
}

const secondaryBtnStyle: React.CSSProperties = {
  padding: 'var(--spacing-3) var(--spacing-4)',
  background: 'transparent',
  color: 'var(--color-text-secondary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

function resultCardStyle(borderColor: string): React.CSSProperties {
  return {
    border: `1px solid ${borderColor}`,
    borderRadius: 'var(--radius-md)',
    padding: 'var(--spacing-4)',
    background: 'var(--color-surface-muted)',
    marginBottom: 'var(--spacing-4)',
  };
}

const errorCardStyle: React.CSSProperties = {
  border: '1px solid var(--color-danger)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--spacing-4)',
  background: 'var(--color-surface-muted)',
  marginBottom: 'var(--spacing-4)',
};

const dtStyle: React.CSSProperties = {
  color: 'var(--color-text-muted)',
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

const codeStyle: React.CSSProperties = {
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
};

function SpinIcon() {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 14,
        height: 14,
        border: '2px solid rgba(255,255,255,0.3)',
        borderTopColor: 'var(--color-text-inverse)',
        borderRadius: '50%',
        display: 'inline-block',
        animation: 'spin 0.8s linear infinite',
      }}
    />
  );
}
